import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import convert from "convert-units";
import { Formik, FormikErrors, FormikProps, FormikTouched } from "formik";
import { Image } from "native-base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Platform, SafeAreaView, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Button, Icon, Input, useTheme } from "react-native-elements";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes, Scale, VerticalScale } from "../../constants";
import { calculateMinimumHealthyWeight } from "../../helpers/fitnessCalculationHelpers";
import { getOrganisation, isFluffyToFitSubdomain, isFuelAndFlowSubdomain } from "../../helpers/userHelpers";
import type { PersonalWeightUnitEnum } from "../../services/backendTypes";
import {
  onboardingDataSelector,
  OnboardingState,
  setPhysicalStats,
  setPreferences,
} from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";
import { updateMacrosInState } from "./onboardingHelpers";

// Helper conversion function (moved outside component)
const convertCmToFtIn = (cm: number): { feet: number; inches: number } => {
  const totalInches = convert(cm).from("cm").to("in");
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

interface PhysicalStatsScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

// FIXME: Can this be taken from the model.py auto-generated types instead?
enum HeightUnit {
  CENTIMETRES = "cm",
  FEET_INCHES = "ftin",
}

interface DefaultUnits {
  heightUnit: HeightUnit;
  weightUnit: PersonalWeightUnitEnum;
}

// Helper function to determine default units specifically for the web platform
const getDefaultUnitsForWeb = (): DefaultUnits => {
  // Just hacking this for now because the locale is not working correctly otherwise
  if (isFuelAndFlowSubdomain() || isFluffyToFitSubdomain()) {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "POUNDS",
    };
  }

  // Only run this logic on the web
  if (Platform.OS !== "web") {
    // Default to metric for non-web platforms (or adjust as needed)
    console.warn("getDefaultUnitsForWeb called on non-web platform, defaulting to metric.");
    return {
      heightUnit: HeightUnit.CENTIMETRES,
      weightUnit: "KILOGRAMS",
    };
  }

  let regionCode: string | undefined;

  try {
    // Use standard browser API navigator.language
    const browserLocale = navigator.language || "nl-NL"; // Provide a fallback to Dutch
    // Use standard Intl.Locale to parse and get the region
    regionCode = new Intl.Locale(browserLocale).region?.toUpperCase();
  } catch (e) {
    console.warn("Could not parse browser locale, defaulting to metric:", e);
    // Fallback to metric if parsing fails
    return {
      heightUnit: HeightUnit.CENTIMETRES,
      weightUnit: "KILOGRAMS",
    };
  }

  // Determine units based on region
  if (regionCode === "US") {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "POUNDS",
    };
  }
  if (regionCode === "GB") {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "STONES",
    };
  }
  // Default to metric for all other regions or if regionCode is undefined
  return {
    heightUnit: HeightUnit.CENTIMETRES,
    weightUnit: "KILOGRAMS",
  };
};

interface WeightUnitModalProps {
  visible: boolean;
  onClose: () => void;
  currentUnit: PersonalWeightUnitEnum;
  onSelect: (unit: PersonalWeightUnitEnum) => void;
  currentKg: number;
  currentGoalKg: number;
  setFieldValue: FormikProps<{
    heightInCm: number;
    weightInKg: number;
    goalWeight: number;
  }>["setFieldValue"];
}

// Helper conversion functions using convert-units (defined within component scope)
const convertFtInToCm = (ftStr: string, inchesStr: string): number => {
  const ftNum = parseFloat(ftStr) || 0;
  const inNum = parseFloat(inchesStr) || 0;
  const totalInches = ftNum * 12 + inNum;
  return Math.round(convert(totalInches).from("in").to("cm"));
};

const convertKgToLbs = (kg: number): number => convert(kg).from("kg").to("lb");

const convertLbsToKg = (lbs: number): number => convert(lbs).from("lb").to("kg");

const convertStonesToKg = (stones: string, pounds: string): number => {
  const totalPounds = (parseFloat(stones) || 0) * 14 + (parseFloat(pounds) || 0);
  return convert(totalPounds).from("lb").to("kg");
};

const convertKgToStonesPounds = (kg: number): { stones: string; pounds: string } => {
  const totalPounds = convert(kg).from("kg").to("lb");
  const stones = Math.floor(totalPounds / 14);
  const pounds = Math.round(totalPounds % 14);
  return { stones: String(stones), pounds: String(pounds) };
};

const PhysicalStatsScreen: React.FC<PhysicalStatsScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { theme } = useTheme();

  const onboardingData = useSelector(onboardingDataSelector);

  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  // Determine default units using the web-specific helper
  // This now correctly handles the web-only focus
  const { heightUnit: defaultHeightUnit, weightUnit: defaultWeightUnit } = getDefaultUnitsForWeb();

  // Initialize state with the determined defaults
  const [heightUnit, setHeightUnit] = useState<HeightUnit>(defaultHeightUnit);
  const [weightUnit, setWeightUnit] = useState<PersonalWeightUnitEnum>(defaultWeightUnit);
  const [weightUnitModalVisible, setWeightUnitModalVisible] = useState(false);

  // Define initial metric values from onboarding data or defaults
  const initialCm = onboardingData.physicalStats?.heightInCm || 160;
  const initialKg = onboardingData.physicalStats?.weightInKg || 70;
  const initialGoalKg = onboardingData.physicalStats?.goalWeight || 65;

  // Convert initial metric values for potential use in imperial state initialization
  const { feet: initialFtNum, inches: initialInNum } = convertCmToFtIn(initialCm);
  const { stones: initialStonesStr, pounds: initialLbsRemainderStr } = convertKgToStonesPounds(initialKg);
  const { stones: initialGoalStonesStr, pounds: initialGoalLbsRemainderStr } = convertKgToStonesPounds(initialGoalKg);

  // Create variables to store the appropriate error messages based on the current unit system
  const heightMinErrorMessage =
    heightUnit === HeightUnit.CENTIMETRES
      ? t("onboarding.onboarding_5_physical_stats_screen.min_height")
      : t("onboarding.onboarding_5_physical_stats_screen.min_height_imperial");

  const heightMaxErrorMessage =
    heightUnit === HeightUnit.CENTIMETRES
      ? t("onboarding.onboarding_5_physical_stats_screen.max_height")
      : t("onboarding.onboarding_5_physical_stats_screen.max_height_imperial");

  const weightMinErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.min_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.min_weight_imperial");

  const weightMaxErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.max_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.max_weight_imperial");

  const goalWeightMaxErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.max_goal_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.max_goal_weight_imperial");

  // Validation schema that uses the unit-appropriate error messages
  const validationSchema = Yup.object().shape({
    heightInCm: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_height"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_height"))
      .min(120, heightMinErrorMessage)
      .max(250, heightMaxErrorMessage),
    weightInKg: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_weight"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_weight"))
      .min(45, weightMinErrorMessage)
      .max(300, weightMaxErrorMessage),
    goalWeight: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_goal_weight"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_goal_weight"))
      .test("minGoalWeight", t("onboarding.onboarding_5_physical_stats_screen.min_goal_weight"), function (value) {
        const { heightInCm } = this.parent;
        return (value || 0) >= calculateMinimumHealthyWeight(heightInCm as number);
      })
      .max(300, goalWeightMaxErrorMessage),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  // Initialize imperial input state based on default units
  const [heightFt, setHeightFt] = useState<string>(
    defaultHeightUnit === HeightUnit.FEET_INCHES ? String(initialFtNum) : ""
  );
  const [heightInches, setHeightInches] = useState<string>(
    defaultHeightUnit === HeightUnit.FEET_INCHES ? String(initialInNum) : ""
  );

  const [weightStones, setWeightStones] = useState<string>(defaultWeightUnit === "STONES" ? initialStonesStr : "");
  const [weightPounds, setWeightPounds] = useState<string>(
    defaultWeightUnit === "STONES" ? initialLbsRemainderStr : ""
  );

  const [goalWeightStones, setGoalWeightStones] = useState<string>(
    defaultWeightUnit === "STONES" ? initialGoalStonesStr : ""
  );
  const [goalWeightPounds, setGoalWeightPounds] = useState<string>(
    defaultWeightUnit === "STONES" ? initialGoalLbsRemainderStr : ""
  );

  const onSubmit = (values: FormSchema): void => {
    dispatch(setPhysicalStats(values));

    // Determine if imperial units should be used for recipes based on the final selected weight unit
    const shouldUseImperialForRecipes = weightUnit === "POUNDS";

    dispatch(
      setPreferences({
        ...onboardingData.preferences,
        usesImperialUnitsForRecipes: shouldUseImperialForRecipes,
        personalWeightUnit: weightUnit,
        usesImperialUnitsForHeight: heightUnit === HeightUnit.FEET_INCHES,
      })
    );

    const updatedOnboardingData: OnboardingState = {
      ...onboardingData,
      physicalStats: values,
    };

    updateMacrosInState(dispatch, updatedOnboardingData);

    // If the goal is weight loss then we need to know their target for weight loss
    // FIXME: Ensure that the logic that is currently done on the weekly goal screen is
    // done here if need be (or at least triggered)
    const nextScreen = onboardingData.goal === "LOSE_WEIGHT" ? Routes.WeeklyGoalScreen : Routes.NutritionalPlanScreen;
    navigation.navigate(nextScreen);
  };

  const getErrorMessage = (
    isGoalWeight: boolean,
    errors: FormikErrors<FormSchema>,
    touched: FormikTouched<FormSchema>
  ): string => {
    if (isGoalWeight) {
      return errors.goalWeight && touched.goalWeight ? errors.goalWeight : "";
    }
    return errors.weightInKg && touched.weightInKg ? errors.weightInKg : "";
  };

  const WeightUnitModal: React.FC<WeightUnitModalProps> = ({
    visible,
    onClose,
    currentUnit,
    onSelect,
    currentKg,
    currentGoalKg,
    setFieldValue,
  }) => {
    const handleUnitSelection = (selectedUnit: PersonalWeightUnitEnum): void => {
      const currentKgValue = parseFloat(String(currentKg)) || 0;
      const currentGoalKgValue = parseFloat(String(currentGoalKg)) || 0;
      const stonesAndPounds = convertKgToStonesPounds(currentKgValue);
      const goalStonesAndPounds = convertKgToStonesPounds(currentGoalKgValue);

      // Handle unit conversion based on selection
      if (selectedUnit === "POUNDS") {
        // No need to do anything special for pounds
      } else if (selectedUnit === "STONES") {
        setWeightStones(stonesAndPounds.stones);
        setWeightPounds(stonesAndPounds.pounds);
        setGoalWeightStones(goalStonesAndPounds.stones);
        setGoalWeightPounds(goalStonesAndPounds.pounds);
      } else if (selectedUnit === "KILOGRAMS") {
        // Round to 1 decimal place for kg
        setFieldValue("weightInKg", Math.round(currentKgValue * 10) / 10);
        setFieldValue("goalWeight", Math.round(currentGoalKgValue * 10) / 10);
      }

      onSelect(selectedUnit);
      onClose();
    };

    return (
      <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
          <View style={styles.modalContent} onStartShouldSetResponder={() => true}>
            <Text style={styles.modalTitle}>
              {t("onboarding.onboarding_5_physical_stats_screen.select_weight_unit")}
            </Text>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => handleUnitSelection("KILOGRAMS")}
              testID="weight-unit-option-KILOGRAMS"
            >
              <View style={styles.radioButton}>
                {currentUnit === "KILOGRAMS" && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>{t("general.kilograms")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => handleUnitSelection("POUNDS")}
              testID="weight-unit-option-POUNDS"
            >
              <View style={styles.radioButton}>
                {currentUnit === "POUNDS" && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>{t("general.pounds")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => handleUnitSelection("STONES")}
              testID="weight-unit-option-STONES"
            >
              <View style={styles.radioButton}>
                {currentUnit === "STONES" && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>{t("general.stones")}</Text>
            </TouchableOpacity>

            <Button title="Close" onPress={onClose} buttonStyle={styles.modalCloseButton} />
          </View>
        </TouchableOpacity>
      </Modal>
    );
  };

  return (
    <Formik
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialValues={validationSchema.cast({
        heightInCm: initialCm, // Use pre-calculated initial metric value
        weightInKg: initialKg, // Use pre-calculated initial metric value
        goalWeight: initialGoalKg, // Use pre-calculated initial metric value
      })}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, errors, touched }) => {
        const toggleHeightUnit = (): void => {
          if (heightUnit === HeightUnit.CENTIMETRES) {
            // Convert current cm value to ft/in using convert-units
            const currentCm = parseFloat(String(values.heightInCm)) || 0;
            const totalInches = convert(currentCm).from("cm").to("in");
            const feet = Math.floor(totalInches / 12);
            const inchesVal = Math.round(totalInches % 12);
            setHeightFt(String(feet));
            setHeightInches(String(inchesVal));
            setHeightUnit(HeightUnit.FEET_INCHES);
          } else {
            setHeightUnit(HeightUnit.CENTIMETRES);
          }
        };

        const openWeightUnitModal = (): void => {
          setWeightUnitModalVisible(true);
        };

        const renderWeightInput = (isGoalWeight: boolean = false): JSX.Element => {
          switch (weightUnit) {
            case "POUNDS":
              return (
                <View style={styles.inputWrapper}>
                  <Input
                    placeholder="Weight in pounds"
                    value={String(Math.round(convertKgToLbs(isGoalWeight ? values.goalWeight : values.weightInKg)))}
                    onChangeText={(text) => {
                      const kg = convertLbsToKg(parseFloat(text) || 0);
                      setFieldValue(isGoalWeight ? "goalWeight" : "weightInKg", kg);
                    }}
                    keyboardType="numeric"
                    containerStyle={styles.inputContainer}
                    inputStyle={styles.input}
                    errorMessage={getErrorMessage(isGoalWeight, errors, touched)}
                    autoCompleteType="off"
                    selectTextOnFocus
                    testID={isGoalWeight ? "goal-weight-pounds-input" : "weight-pounds-input"}
                  />
                  <Text style={styles.unitText}>lb</Text>
                </View>
              );
            case "STONES":
              return (
                <View style={styles.ftInContainer}>
                  <View style={styles.ftInputWrapper}>
                    <Input
                      containerStyle={styles.ftInputContainer}
                      inputContainerStyle={styles.ftInputInnerContainer}
                      placeholder="Stones"
                      value={isGoalWeight ? goalWeightStones : weightStones}
                      onChangeText={(text) => {
                        if (isGoalWeight) {
                          setGoalWeightStones(text);
                          const kg = convertStonesToKg(text, goalWeightPounds);
                          setFieldValue("goalWeight", kg);
                        } else {
                          setWeightStones(text);
                          const kg = convertStonesToKg(text, weightPounds);
                          setFieldValue("weightInKg", kg);
                        }
                      }}
                      keyboardType="numeric"
                      autoCompleteType="off"
                      inputStyle={[styles.input, { paddingRight: 40 }]}
                      rightIcon={<Text style={styles.unitText}>st</Text>}
                      rightIconContainerStyle={styles.ftInRightIcon}
                      testID={isGoalWeight ? "goal-weight-stones-input" : "weight-stones-input"}
                    />
                  </View>
                  <View style={styles.ftInputWrapper}>
                    <Input
                      containerStyle={styles.ftInputContainer}
                      inputContainerStyle={styles.ftInputInnerContainer}
                      placeholder="Pounds"
                      value={isGoalWeight ? goalWeightPounds : weightPounds}
                      onChangeText={(text) => {
                        if (isGoalWeight) {
                          setGoalWeightPounds(text);
                          const kg = convertStonesToKg(goalWeightStones, text);
                          setFieldValue("goalWeight", kg);
                        } else {
                          setWeightPounds(text);
                          const kg = convertStonesToKg(weightStones, text);
                          setFieldValue("weightInKg", kg);
                        }
                      }}
                      keyboardType="numeric"
                      autoCompleteType="off"
                      inputStyle={[styles.input, { paddingRight: 40 }]}
                      rightIcon={<Text style={styles.unitText}>lb</Text>}
                      rightIconContainerStyle={styles.ftInRightIcon}
                      testID={isGoalWeight ? "goal-weight-pounds-remainder-input" : "weight-pounds-remainder-input"}
                    />
                  </View>
                </View>
              );
            case "KILOGRAMS":
            default:
              return (
                <View style={styles.inputWrapper}>
                  <Input
                    placeholder={t(
                      isGoalWeight
                        ? "onboarding.onboarding_5_physical_stats_screen.goal_weight_placeholder"
                        : "onboarding.onboarding_5_physical_stats_screen.weight_placeholder"
                    )}
                    value={String(isGoalWeight ? values.goalWeight : values.weightInKg)}
                    onChangeText={(text) => setFieldValue(isGoalWeight ? "goalWeight" : "weightInKg", text)}
                    keyboardType="decimal-pad"
                    containerStyle={styles.inputContainer}
                    inputStyle={styles.input}
                    errorMessage={getErrorMessage(isGoalWeight, errors, touched)}
                    autoCompleteType="off"
                    selectTextOnFocus
                    testID={isGoalWeight ? "goal-weight-input" : "weight-input"}
                  />
                  <Text style={styles.unitText}>kg</Text>
                </View>
              );
          }
        };

        return (
          <SafeAreaView style={styles.container}>
            <View style={styles.header}>
              <Icon name="arrow-back" size={24} onPress={() => navigation.goBack()} tvParallaxProperties={undefined} />
            </View>
            <ScrollView contentContainerStyle={styles.content}>
              {logoUri ? (
                <Image
                  source={{ uri: logoUri }}
                  style={{ width: Scale(160), height: VerticalScale(60) }}
                  resizeMode="contain"
                />
              ) : (
                <Icon
                  name="robot"
                  type="material-community"
                  size={64}
                  color={theme.colors?.primary}
                  tvParallaxProperties={undefined}
                />
              )}

              <Text style={styles.title}>{t("onboarding.onboarding_5_physical_stats_screen.title")}</Text>
              <Text style={styles.subtitle}>{t("onboarding.onboarding_5_physical_stats_screen.subtitle")}</Text>

              {/* Height Title */}
              <Text style={styles.title}>
                <Text style={{ fontSize: styles.title.fontSize * 1.3 }}>{"📏 "}</Text>
                {t("onboarding.onboarding_5_physical_stats_screen.height_title")}
              </Text>

              {/* Conditional Rendering for Height Input */}
              {heightUnit === HeightUnit.CENTIMETRES ? (
                <View style={styles.inputWrapper}>
                  <Input
                    placeholder={t("onboarding.onboarding_5_physical_stats_screen.height_placeholder")}
                    value={String(values.heightInCm)}
                    onChangeText={(text) => setFieldValue("heightInCm", text)}
                    keyboardType="numeric"
                    containerStyle={styles.inputContainer}
                    inputStyle={styles.input}
                    errorMessage={errors.heightInCm && touched.heightInCm ? errors.heightInCm : ""}
                    autoCompleteType="off"
                    selectTextOnFocus
                    testID="height-input"
                  />
                  <Text style={styles.unitText}>{t("onboarding.onboarding_5_physical_stats_screen.centimeters")}</Text>
                </View>
              ) : (
                // Feet/inches
                <View style={styles.ftInContainer}>
                  <View style={styles.ftInputWrapper}>
                    <Input
                      containerStyle={styles.ftInputContainer}
                      inputContainerStyle={styles.ftInputInnerContainer}
                      placeholder="Feet"
                      value={heightFt}
                      onChangeText={(text) => {
                        setHeightFt(text);
                        const newCm = convertFtInToCm(text, heightInches);
                        setFieldValue("heightInCm", newCm);
                      }}
                      keyboardType="numeric"
                      autoCompleteType="off"
                      inputStyle={styles.input}
                      rightIcon={<Text style={styles.unitText}>ft</Text>}
                      rightIconContainerStyle={styles.ftInRightIcon}
                      testID="height-feet-input"
                    />
                  </View>
                  <View style={styles.ftInputWrapper}>
                    <Input
                      containerStyle={styles.ftInputContainer}
                      inputContainerStyle={styles.ftInputInnerContainer}
                      placeholder="Inches"
                      value={heightInches}
                      onChangeText={(text) => {
                        setHeightInches(text);
                        const newCm = convertFtInToCm(heightFt, text);
                        setFieldValue("heightInCm", newCm);
                      }}
                      keyboardType="numeric"
                      autoCompleteType="off"
                      inputStyle={styles.input}
                      rightIcon={<Text style={styles.unitText}>in</Text>}
                      rightIconContainerStyle={styles.ftInRightIcon}
                      testID="height-inches-input"
                    />
                  </View>
                </View>
              )}

              {/* Toggle Link */}
              <Text style={styles.link} onPress={toggleHeightUnit} testID="toggle-height-unit">
                {heightUnit === HeightUnit.CENTIMETRES
                  ? t("onboarding.onboarding_5_physical_stats_screen.change_units_to_imperial")
                  : t("onboarding.onboarding_5_physical_stats_screen.change_units_to_metric")}
              </Text>
              {/* Display error message for heightInCm if any (applies to both modes) */}
              {errors.heightInCm && touched.heightInCm ? (
                <Text style={styles.errorText}>{errors.heightInCm}</Text>
              ) : null}

              {/* Weight Input */}
              <Text style={styles.title}>
                <Text style={{ fontSize: styles.title.fontSize * 1.3 }}>{"⚖️ "}</Text>
                {t("onboarding.onboarding_5_physical_stats_screen.weight_title")}
              </Text>
              <Text style={styles.subtitle}>{t("onboarding.onboarding_5_physical_stats_screen.weight_subtitle")}</Text>
              {renderWeightInput(false)}

              <Button
                title={t("onboarding.onboarding_5_physical_stats_screen.change_units_for_weight")}
                type="outline"
                onPress={openWeightUnitModal}
                buttonStyle={styles.unitButton}
                titleStyle={styles.unitButtonText}
                testID="change-weight-unit-button"
              />

              {/* Weight Unit Selection Modal */}
              <WeightUnitModal
                visible={weightUnitModalVisible}
                onClose={() => setWeightUnitModalVisible(false)}
                currentUnit={weightUnit}
                onSelect={setWeightUnit}
                currentKg={values.weightInKg}
                currentGoalKg={values.goalWeight}
                setFieldValue={setFieldValue}
              />

              {/* Goal Weight Input */}
              <Text style={styles.title}>
                <Text style={{ fontSize: styles.title.fontSize * 1.3 }}>{"🎯 "}</Text>
                {t("onboarding.onboarding_5_physical_stats_screen.goal_weight_title")}
              </Text>
              <Text style={styles.subtitle}>
                {t("onboarding.onboarding_5_physical_stats_screen.goal_weight_subtitle")}
              </Text>
              {renderWeightInput(true)}

              <Button
                title={t("onboarding.onboarding_5_physical_stats_screen.next_button")}
                buttonStyle={styles.nextButton}
                onPress={() => handleSubmit()}
                testID="next-button"
              />
            </ScrollView>
          </SafeAreaView>
        );
      }}
    </Formik>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  progressText: {
    marginLeft: 16,
    fontWeight: "bold",
  },
  content: {
    alignItems: "center",
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginTop: 16,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    color: "#777",
    textAlign: "center",
    marginVertical: 8,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 8,
    paddingHorizontal: 40,
    width: "100%",
  },
  inputContainer: {
    flex: 1,
    maxWidth: "70%",
  },
  input: {
    textAlign: "center",
    color: "#333",
    fontSize: 16,
  },
  nextButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    marginTop: 16,
    borderRadius: 8,
    width: "80%",
    alignSelf: "center",
  },
  link: {
    color: "blue",
    textDecorationLine: "underline",
    marginTop: 8,
    textAlign: "center",
  },
  errorText: {
    color: "red",
    marginTop: 4,
    textAlign: "center",
  },
  // Container for both ft/in Inputs
  ftInContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginVertical: 8,
    paddingHorizontal: 20,
  },
  // Wrapper for each Input
  ftInputWrapper: {
    flex: 1,
    maxWidth: "45%", // Limit width to ensure both inputs fit
    marginHorizontal: 5,
  },
  // Force the Input's outer container to use the full width of its wrapper
  ftInputContainer: {
    width: "100%",
    paddingHorizontal: 0,
  },
  // Override the Input's internal container
  ftInputInnerContainer: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  // Right icon styling
  ftInRightIcon: {
    marginRight: 5,
    position: "absolute",
    right: 0,
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  // Existing unit text style (unchanged)
  unitText: {
    fontSize: 16,
    color: "#777",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 10,
    padding: 20,
    width: "80%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    textAlign: "center",
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
  },
  radioButton: {
    height: 20,
    width: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#007BFF",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  radioButtonSelected: {
    height: 10,
    width: 10,
    borderRadius: 5,
    backgroundColor: "#007BFF",
  },
  radioText: {
    fontSize: 16,
  },
  modalCloseButton: {
    marginTop: 15,
  },
  unitButton: {
    marginTop: 8,
    marginBottom: 16,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  unitButtonText: {
    fontSize: 14,
  },
});

export default PhysicalStatsScreen;
